import { extend, uid, isDefined } from "@dhx/ts-common/core";
import { el } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { focusManager } from "@dhx/ts-common/FocusManager";
import { IHandlers } from "@dhx/ts-common/types";
import { Layout } from "@dhx/ts-layout";
import { Toolbar } from "@dhx/ts-toolbar";
import { detectDrag } from "./helpers";
import {
	IDirectionConfig,
	IWindowConfig,
	WindowEvents,
	IWindow,
	ISize,
	IPosition,
	IWindowEventHandlersMap,
} from "./types";
import WindowController from "./WindowController";
import { NavigationBarEvents } from "@dhx/ts-navbar";

export class Window implements IWindow {
	public config: IWindowConfig;
	public events: EventSystem<WindowEvents, IWindowEventHandlersMap>;
	public header: Toolbar;
	public footer: Toolbar;

	protected _layout: Layout;
	protected _handlers: IHandlers;
	protected _popup: HTMLElement;
	protected _uid: string;
	private _isActive: boolean;
	private _blocker: HTMLElement;
	private _keyManager: IKeyManager;
	private _fullScreen: boolean;
	private _oldSizes: { width: number; height: number };
	private _oldPosition: { left: number; top: number };

	constructor(config: IWindowConfig) {
		this.config = extend(
			{
				movable: false,
				resizable: false,
				closable: config.modal,
			},
			config
		);

		if (this.config.node && typeof this.config.node === "string") {
			this.config.node = document.getElementById(this.config.node);
		} else if (!this.config.node) {
			this.config.node = document.body;
		}
		this._uid = uid();
		this.events = new EventSystem(this);

		const popup = (this._popup = document.createElement("div"));
		popup.tabIndex = 1;
		popup.setAttribute("role", "dialog");
		popup.setAttribute("aria-modal", `${this.config.modal || false}`);

		if (!this.config.modal) {
			WindowController.add(this._uid, this._popup);
		}
		this._fullScreen = false;
		this._isActive = false;
		this._keyManager = new KeyManager((_, focusId) => focusId === this._uid);

		this._initHandlers();
		this._initUI();

		if (this.config.html) {
			this.attachHTML(this.config.html);
		}
	}
	paint(): void {
		this._layout.paint();
	}
	isFullScreen(): boolean {
		return this._fullScreen;
	}
	setFullScreen(): void {
		if (!this._fullScreen) {
			this._fullScreen = true;
			this.setSize(window.innerWidth, window.innerHeight);
			this.setPosition(window.pageXOffset, window.pageYOffset);
		}
	}
	unsetFullScreen(): void {
		if (this._fullScreen) {
			this._fullScreen = false;
			this.setSize(this._oldSizes.width, this._oldSizes.height);
			this.setPosition(this._oldPosition.left, this._oldPosition.top);
		}
	}
	setSize(width: number, height: number): void {
		this._oldSizes = {
			width: this._popup.offsetWidth,
			height: this._popup.offsetHeight,
		};
		const size = {
			width: this._oldSizes.width,
			height: this._oldSizes.height,
		};
		if (isDefined(width)) {
			this.config.width = size.width = width;
		}
		if (isDefined(height)) {
			this.config.height = size.height = height;
		}

		this._popup.style.width = size.width + "px";
		this._popup.style.height = size.height + "px";

		this.events.fire(WindowEvents.resize, [
			size,
			this._oldSizes,
			{ left: true, top: true, bottom: true, right: true },
		]);
	}
	getSize(): ISize {
		return {
			width: this._popup.offsetWidth,
			height: this._popup.offsetHeight,
		};
	}
	setPosition(left: number, top: number): void {
		this._oldPosition = {
			left: this._popup.offsetLeft,
			top: this._popup.offsetTop,
		};
		const position = {
			left: this._oldPosition.left,
			top: this._oldPosition.top,
		};
		if (isDefined(left)) {
			this.config.left = position.left = left;
		}
		if (isDefined(top)) {
			this.config.top = position.top = top;
		}

		this._popup.style.left = position.left + "px";
		this._popup.style.top = position.top + "px";

		this.events.fire(WindowEvents.resize, [
			position,
			this._oldPosition,
			{ left: true, top: true, bottom: true, right: true },
		]);
	}
	getPosition(): IPosition {
		return {
			left: this._popup.offsetLeft,
			top: this._popup.offsetTop,
		};
	}
	show(left: number = this.config.left, top: number = this.config.top): void {
		if (!this.events.fire(WindowEvents.beforeShow, [{ left, top }])) {
			return;
		}

		if (this.isVisible()) {
			this.hide();
		}

		WindowController.setActive(this._uid);
		this._popup.className +=
			" dhx_popup dhx_widget" +
			(this.config.modal ? " dhx_popup--window_modal" : " dhx_popup--window") +
			(this.config.css ? " " + this.config.css : "");
		this._popup.style.position = this.config.modal ? "fixed" : "absolute";
		this._popup.setAttribute("data-dhx-widget-id", this._uid);
		const {
			containerInnerWidth,
			containerInnerHeight,
			containerXOffset,
			containerYOffset,
		} = this._getContainerParams();

		const width = (this.config.width =
			(typeof this.config.width === "number" && this.config.width) ||
			(typeof this.config.minWidth === "number" && this.config.minWidth) ||
			containerInnerWidth / 2);
		const height = (this.config.height =
			(typeof this.config.height === "number" && this.config.height) ||
			(typeof this.config.minHeight === "number" && this.config.minHeight) ||
			containerInnerHeight / 2);

		this.config.left = left = isDefined(left)
			? left
			: (containerInnerWidth - width) / (this.config.modal ? 2 : 2 + containerXOffset);
		this.config.top = top = isDefined(top)
			? top
			: (containerInnerHeight - height) / (this.config.modal ? 2 : 2 + containerYOffset);

		if (this._isActive) {
			this._popup.style.left = left + "px";
			this._popup.style.top = top + "px";
			return;
		}
		if (this.config.viewportOverflow) {
			WindowController.openFreeWindow(this.config.node);
		}
		if (this.config.modal) {
			this._blockScreen();
		}

		this._popup.style.width = width + "px";
		this._popup.style.height = height + "px";
		this._popup.style.left = left + "px";
		this._popup.style.top = top + "px";
		this.config.node.appendChild(this._popup);
		this._popup.focus();
		this._isActive = true;
		this.events.fire(WindowEvents.afterShow, [{ left, top }]);
		focusManager.setFocusId(this._uid);
	}
	hide(): void {
		this._hide();
	}
	protected _hide(e?: Event) {
		if (
			!this._isActive ||
			!this.events.fire(WindowEvents.beforeHide, [{ left: this.config.left, top: this.config.top }, e])
		) {
			return;
		}
		if (this.config.viewportOverflow) {
			WindowController.closeFreeWindow(this.config.node);
		}
		if (this._blocker) {
			this.config.node.removeChild(this._blocker);
			if (this.config.closable) {
				this._keyManager.removeHotKey(null, this);
			}
			this._blocker = null;
		}
		this.config.node.removeChild(this._popup);
		this._isActive = false;
		this.events.fire(WindowEvents.afterHide, [{ left: this.config.left, top: this.config.top }, e]);
	}
	isVisible(): boolean {
		return this._isActive;
	}
	getWidget(): any {
		return this._layout.getCell("content").getWidget();
	}
	getContainer(): HTMLElement {
		return this.getRootView().data._container;
	}
	attach(name: any, config?: any): void {
		this._layout.getCell("content").attach(name, config);
	}
	attachHTML(html: string): void {
		this._layout.getCell("content").attachHTML(html);
	}
	getRootView() {
		return this._layout.getRootView();
	}
	destructor(): void {
		this._isActive && this.hide();
		this.header && this.header.destructor();
		this.footer && this.footer.destructor();
		this.events && this.events.clear();

		this._keyManager && this._keyManager.destructor();
		this._layout && this._layout.destructor();

		this.config = this.header = this.footer = this.events = null;
		this._popup = this._uid = this._handlers = this._isActive = this._keyManager = this._fullScreen = this._oldSizes = this._oldPosition = null;
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	fullScreen(): void {
		this.setFullScreen();
	}
	protected _initHandlers() {
		this._handlers = {
			headerDblClick: (e: Event) => this.events.fire(WindowEvents.headerDoubleClick, [e]),
			move: e => {
				if (e.which === 3) {
					return;
				}
				e.preventDefault();
				detectDrag(e).then(pos => {
					if (pos) {
						this._startDrag(pos.x, pos.y);
					}
				});
			},
			resize: {
				".dhx_window-resizer": e => {
					if (e.which === 3) {
						return;
					}
					e.preventDefault();
					WindowController.setActive(this._uid);

					detectDrag(e).then(pos => {
						if (pos) {
							const classList = (e.target as HTMLElement).classList;
							if (classList.contains("dhx_window-resizer--left")) {
								this._startResize({ left: true });
							} else if (classList.contains("dhx_window-resizer--right")) {
								this._startResize({ right: true });
							} else if (classList.contains("dhx_window-resizer--top")) {
								this._startResize({ top: true });
							} else if (classList.contains("dhx_window-resizer--bottom")) {
								this._startResize({ bottom: true });
							} else if (classList.contains("dhx_window-resizer--bottom_left")) {
								this._startResize({ left: true, bottom: true });
							} else if (classList.contains("dhx_window-resizer--bottom_right")) {
								this._startResize({ bottom: true, right: true });
							} else if (classList.contains("dhx_window-resizer--top_left")) {
								this._startResize({ top: true, left: true });
							} else if (classList.contains("dhx_window-resizer--top_right")) {
								this._startResize({ top: true, right: true });
							}
						}
					});
				},
			},
			setActive: () => {
				WindowController.setActive(this._uid);
			},
		};
	}
	protected _initUI() {
		const rows: any = [];
		const isHeader =
			(this.config.header || this.config.title || this.config.closable || this.config.movable) &&
			this.config.header !== false;

		if (isHeader) {
			rows.push({
				id: "header",
				height: "content",
				css: "dhx_window-header " + (this.config.movable ? "dhx_window-header--movable" : ""),
				on: {
					mousedown: this.config.movable && this._handlers.move,
					dblclick: this._handlers.headerDblClick,
				},
			});
		}
		rows.push({
			id: "content",
			css: isHeader ? "dhx_window-content" : "dhx_window-content-without-header",
		});
		if (this.config.footer) {
			rows.push({ id: "footer", height: "content", css: "dhx_window-footer" });
		}
		if (this.config.resizable) {
			rows.push({ id: "resizers", height: "content", css: "resizers" });
		}
		const layout = (this._layout = new Layout(this._popup, {
			css: "dhx_window" + (this.config.modal ? " dhx_window--modal" : ""),
			rows,
			on: {
				mousedown: this._handlers.setActive,
			},
			id: this._uid,
		}));
		if (isHeader) {
			const header = (this.header = new Toolbar());
			if (this.config.title) {
				this.header.data.add({
					type: "title",
					value: this.config.title,
					id: "title",
					css: "title_max",
				});
				this._popup.setAttribute("aria-label", this.config.title);
			}
			if (this.config.closable) {
				this.header.data.add({
					type: "spacer",
				});
				this.header.data.add({
					id: "close",
					type: "button",
					view: "link",
					size: "medium",
					color: "secondary",
					circle: true,
					icon: "dxi dxi-close",
				});
				header.events.on(NavigationBarEvents.click, (id, e) => {
					if (id === "close") {
						this._hide(e);
					}
				});
			}
			layout.getCell("header").attach(header);
		}
		if (this.config.footer) {
			const footer = (this.footer = new Toolbar());
			layout.getCell("footer").attach(footer);
		}
		if (this.config.resizable) {
			layout.getCell("resizers").attach(() => this._drawResizers());
		}
	}
	protected _drawResizers() {
		return el(
			".dhx-resizers",
			{
				onmousedown: this._handlers.resize,
			},
			[
				el(".dhx_window-resizer.dhx_window-resizer--left", { _ref: "left" }),
				el(".dhx_window-resizer.dhx_window-resizer--right", { _ref: "right" }),
				el(".dhx_window-resizer.dhx_window-resizer--bottom", { _ref: "bottom" }),
				el(".dhx_window-resizer.dhx_window-resizer--top", { _ref: "top" }),
				el(".dhx_window-resizer.dhx_window-resizer--bottom_right", { _ref: "bottomRight" }),
				el(".dhx_window-resizer.dhx_window-resizer--bottom_left", { _ref: "bottomLeft" }),
				el(".dhx_window-resizer.dhx_window-resizer--top_right", { _ref: "topRight" }),
				el(".dhx_window-resizer.dhx_window-resizer--top_left", { _ref: "topLeft" }),
			]
		);
	}
	private _startDrag(x: number, y: number) {
		this.config.node.classList.add("dhx_window--stop_selection");

		const deltaX = x - this._popup.offsetLeft;
		const deltaY = y - this._popup.offsetTop;
		const width = this._popup.offsetWidth;
		const height = this._popup.offsetHeight;

		const mousemove = e => {
			const oldposition = {
				left: this._popup.offsetLeft,
				top: this._popup.offsetTop,
			};

			let newX = e.pageX - deltaX;
			let newY = e.pageY - deltaY;

			if (!this.config.viewportOverflow) {
				const {
					containerXOffset,
					containerYOffset,
					containerInnerWidth,
					containerInnerHeight,
				} = this._getContainerParams();
				if (newX < containerXOffset) {
					newX = containerXOffset;
				} else if (newX > containerXOffset + containerInnerWidth - width) {
					newX = containerXOffset + containerInnerWidth - width;
				}
				if (newY < containerYOffset) {
					newY = containerYOffset;
				} else if (newY > containerYOffset + containerInnerHeight - height) {
					newY = containerYOffset + containerInnerHeight - height;
				}
			}
			this.config.left = newX;
			this.config.top = newY;
			this._popup.style.left = newX + "px";
			this._popup.style.top = newY + "px";
			const position = { left: newX, top: newY };
			this.events.fire(WindowEvents.move, [
				position,
				oldposition,
				{ left: true, top: true, bottom: true, right: true },
			]);
		};
		const mouseup = () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
			this.config.node.classList.remove("dhx_window--stop_selection");
		};
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousemove", mousemove);
	}
	protected _startResize(resizeConfig: IDirectionConfig) {
		const minWidth = this.config.minWidth | 100;
		const minHeight = this.config.minHeight | 100;
		const left = this._popup.offsetLeft;
		const top = this._popup.offsetTop;
		const width = this._popup.offsetWidth;
		const height = this._popup.offsetHeight;
		const root = this.getRootView().refs;

		let resizer;
		let resizeClassName;
		switch (true) {
			case resizeConfig.bottom && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--bottom_left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.bottom && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--bottom_right";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.top && resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--top_left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.top && resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--top-right";
				resizer = root.right;
				break;
			case resizeConfig.top:
				resizeClassName = "dhx_window-body-pointer--top";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.bottom:
				resizeClassName = "dhx_window-body-pointer--bottom";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.left:
				resizeClassName = "dhx_window-body-pointer--left";
				resizer = root.bottomLeft;
				break;
			case resizeConfig.right:
				resizeClassName = "dhx_window-body-pointer--right";
				resizer = root.right;
				break;
		}
		resizer.el.classList.add("dhx_window-resizer--active");
		this.config.node.classList.add("dhx_window--stop_selection");
		this.config.node.classList.add(resizeClassName);
		const mousemove = (e: MouseEvent) => {
			const {
				containerInnerWidth,
				containerInnerHeight,
				containerXOffset,
				containerYOffset,
			} = this._getContainerParams();
			const size = {
				width: this._notInNode() ? e.pageX - left : e.pageX - this.config.node.offsetLeft - left,
				height: this._notInNode() ? e.pageY - top : e.pageY - this.config.node.offsetTop - top,
				left: this._notInNode() ? e.pageX : e.pageX - this.config.node.offsetLeft,
				top: this._notInNode() ? e.pageY : e.pageY - this.config.node.offsetTop,
			};
			if (resizeConfig.right) {
				if (size.width < minWidth) {
					size.width = minWidth;
				} else if (size.width > containerXOffset + containerInnerWidth - left) {
					size.width = containerXOffset + containerInnerWidth - left;
				}

				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.bottom) {
				if (size.height < minHeight) {
					size.height = minHeight;
				} else if (size.height > containerYOffset + containerInnerHeight - top) {
					size.height = containerYOffset + containerInnerHeight - top;
				}

				this._popup.style.height = size.height + "px";
			}
			if (resizeConfig.left) {
				if (left + width - size.left < minWidth) {
					size.left = left + width - minWidth;
				}
				size.width = left + width - size.left;

				this.config.left = size.left;
				this._popup.style.left = size.left + "px";
				this._popup.style.width = size.width + "px";
			}
			if (resizeConfig.top) {
				if (size.top < containerYOffset) {
					size.top = containerYOffset;
				} else if (top + height - size.top < minHeight) {
					size.top = top + height - minHeight;
				}
				size.height = top + height - size.top;

				this.config.top = size.top;
				this._popup.style.top = size.top + "px";
				this._popup.style.height = size.height + "px";
			}
			this.config.width = this._popup.offsetWidth;
			this.config.height = this._popup.offsetHeight;
			this.events.fire(WindowEvents.resize, [size, { left, top, height, width }, resizeConfig]);
		};
		const mouseup = () => {
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
			this.config.node.classList.remove("dhx_window--stop_selection");
			this.config.node.classList.remove(resizeClassName);
			resizer.el.classList.remove("dhx_window-resizer--active");
		};
		document.addEventListener("mouseup", mouseup);
		document.addEventListener("mousemove", mousemove);
	}
	private _blockScreen() {
		const blocker = document.createElement("div");
		blocker.className = "dhx_window__overlay";
		this.config.node.appendChild(blocker);
		this._blocker = blocker;

		if (this.config.closable) {
			blocker.addEventListener("click", e => this._hide(e));
			this._keyManager.addHotKey("escape", (e: KeyboardEvent) => {
				const collection1 = Array.prototype.slice.call(
					document.querySelectorAll(".dhx_popup--window_modal")
				);
				const collection2 = Array.prototype.slice.call(
					document.querySelectorAll(".dhx_popup--window")
				);
				const mainCollection = collection1.concat(collection2);
				if (mainCollection.length === 1) {
					this._hide(e);
					return;
				}
				mainCollection.sort((a, b) => {
					return +window.getComputedStyle(b).zIndex - +window.getComputedStyle(a).zIndex;
				});
				if (mainCollection[mainCollection.length - 1] === this._popup) {
					this._hide(e);
				}
				return;
			});
		}
	}
	protected _notInNode() {
		return this.config.node === document.body || this.config.modal;
	}
	protected _getContainerParams() {
		const notInNode = this._notInNode();
		return {
			containerInnerWidth: notInNode ? window.innerWidth : this.config.node.offsetWidth,
			containerInnerHeight: notInNode ? window.innerHeight : this.config.node.offsetHeight,
			containerXOffset: notInNode ? window.pageXOffset : this.config.node.scrollLeft,
			containerYOffset: notInNode ? window.pageYOffset : this.config.node.scrollTop,
		};
	}
}
